import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { generateBreadcrumbSchema } from '@/mocks/schema';

const BASE_URL = 'https://yuriclub.cn';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 | 百合会"
          description="抱歉，您访问的文章不存在或已被移除。"
          keywords="百合会"
          canonicalUrl={`${BASE_URL}/article/${slug}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，你访问的文章不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} | 百合会`;
  const pageDesc = article.excerpt;
  const pageKeywords = article.tags.join(',') + ',百合会,百合文化';
  const canonicalUrl = `${BASE_URL}/article/${slug}`;

  const jsonLd = [
    generateBreadcrumbSchema(
      [
        { label: '首页', href: '/' },
        { label: article.categoryName, href: `/category/${article.category}` },
        { label: article.title },
      ],
      BASE_URL
    ),
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '百合会',
        url: BASE_URL,
      },
      url: canonicalUrl,
    },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.coverImage}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-14 md:pb-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto mt-6">
          <div className="mb-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-block px-3 py-1.5 rounded-md text-xs font-medium bg-primary-100 text-primary-700 mb-4 hover:bg-primary-200 transition-colors"
            >
              {article.categoryName}
            </Link>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-xs text-primary-600"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-400"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-foreground-400"></i>
                阅读约 {article.readTime} 分钟
              </span>
            </div>
          </div>

          <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="article-content prose max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-sm font-semibold text-foreground-500 mb-3">标签</h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto mt-14 md:mt-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
                >
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{ra.author}</span>
                      <span>{ra.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}